import React, { useMemo, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import {
  CardComp,
  ButtonComp,
  ModalCompAdvanceUpdate,
  ConfirmationModal,
} from "../../../components";
import { TableCompData } from "../../../components/Table";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { formatMoney, convertToTitleCase } from "../../../utils";
import {
  useGetStaffBankTransferPaymentsQuery,
  useGetStaffBankTransferPaymentDetailQuery,
  useConfirmStaffBankTransferPaymentMutation,
  useRejectStaffBankTransferPaymentMutation,
} from "../../../store/admin/bankTransferSlice";

function statusBadge(status) {
  const s = String(status || "").toUpperCase();
  let bg = "#A1A0A0";
  if (s === "AWAITING_BANK_CONFIRMATION") bg = "#ff9500";
  if (s === "SUCCESSFUL") bg = "#19bc31";
  if (s === "REJECTED") bg = "#ff4343";
  return (
    <span
      className="rounded-pill text-white px-2 py-1 small"
      style={{ backgroundColor: bg, whiteSpace: "nowrap" }}
    >
      {convertToTitleCase(String(status || "").replace(/_/g, " "))}
    </span>
  );
}

function parseJsonMaybe(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch (_err) {
    return null;
  }
}

function toArrayFromUnknown(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string") {
    const parsed = parseJsonMaybe(value);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
    return value.trim() ? [value.trim()] : [];
  }
  return [];
}

function getReceiptLinks(detail) {
  const paymentData = parseJsonMaybe(detail?.payment_data);
  const metadata = parseJsonMaybe(detail?.metadata);

  const receiptSources = [
    detail?.receipt_url,
    detail?.receipt_path,
    paymentData?.receipt_url,
    paymentData?.receipt_path,
    metadata?.receipt_url,
    metadata?.receipt_path,
  ].filter(Boolean);

  const receiptPaths = [
    ...toArrayFromUnknown(detail?.receipt_paths),
    ...toArrayFromUnknown(detail?.bank_transfer_receipt_paths),
    ...toArrayFromUnknown(paymentData?.receipt_paths),
    ...toArrayFromUnknown(paymentData?.bank_transfer_receipt_paths),
    ...toArrayFromUnknown(metadata?.receipt_paths),
  ].filter(Boolean);

  return [...new Set([...receiptSources, ...receiptPaths])];
}

function isPdfUrl(url) {
  const normalized = String(url || "").toLowerCase();
  return normalized.includes(".pdf") || normalized.includes("application/pdf");
}

export default function BankTransferConfirmations() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [modalPage, setModalPage] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const { data: listData, isFetching } = useGetStaffBankTransferPaymentsQuery({
    status: statusFilter,
    search,
    page,
    limit: 20,
  });

  const { data: detail, isFetching: detailLoading } =
    useGetStaffBankTransferPaymentDetailQuery(detailId, {
      skip: !detailId,
    });
  const isLoanRepayment =
    parseJsonMaybe(detail?.metadata)?.payment_kind === "loan_repayment";

  const [confirmPayment, { isLoading: confirming }] =
    useConfirmStaffBankTransferPaymentMutation();
  const [rejectPayment, { isLoading: rejecting }] =
    useRejectStaffBankTransferPaymentMutation();

  const rows = Array.isArray(listData?.data) ? listData.data : [];
  const pagination = listData || {};

  const columns = useMemo(
    () => [
      {
        name: "S/N",
        width: "70px",
        selector: (row, index) => {
          const perPage = pagination?.per_page || 20;
          const currentPage = pagination?.current_page || page;
          return (
            <span className="text-muted fw-2" style={{ fontSize: "14px" }}>
              {(currentPage - 1) * perPage + index + 1}
            </span>
          );
        },
        sortable: false,
      },
      {
        name: "Reference",
        minWidth: "200px",
        selector: (row) => row.payment_reference,
      },
      {
        name: "Member",
        minWidth: "160px",
        cell: (row) =>
          row.member
            ? `${row.member.first_name || ""} ${row.member.last_name || ""}`.trim() ||
              row.member.member_no
            : "—",
      },
      {
        name: "Amount",
        minWidth: "120px",
        selector: (row) =>
          formatMoney(Number(row.total_amount || 0).toFixed(2), true),
      },
      {
        name: "Status",
        minWidth: "140px",
        cell: (row) => statusBadge(row.status),
      },
      {
        name: "Submitted",
        minWidth: "120px",
        selector: (row) =>
          row.created_at
            ? moment(row.created_at).format("DD-MMM-YYYY HH:mm")
            : "—",
      },
      {
        name: "Receipt",
        minWidth: "170px",
        cell: (row) => {
          const receiptLinks = getReceiptLinks(row);
          if (receiptLinks.length === 0) {
            return <span className="small text-muted">No receipt added.</span>;
          }

          return (
            <div className="d-flex flex-column gap-1">
              <a
                href={receiptLinks[0]}
                target="_blank"
                rel="noreferrer"
                className="small fw-semibold text-secondary2"
                style={{ textDecoration: "underline" }}
              >
                View receipt
              </a>
              {receiptLinks.length > 1 && (
                <span className="small text-muted">+{receiptLinks.length - 1} more</span>
              )}
            </div>
          );
        },
      },
      {
        name: "Action",
        minWidth: "120px",
        cell: (row) => (
          <ButtonComp
            type="button"
            btnText="View"
            btnClassName="border border-primary1 text-primary1 px-3 py-1 rounded bg-white"
            ButtonInlineStyle={{ whiteSpace: "nowrap", minWidth: "fit-content", fontSize: "13px" }}
            onClick={() => {
              setDetailId(row.id);
              setModalPage("Detail");
            }}
          />
        ),
      },
    ],
    [page, pagination?.current_page, pagination?.per_page]
  );

  const handleConfirm = async () => {
    if (!detailId) return;
    try {
      await confirmPayment({
        id: detailId,
        acknowledgement: true,
      }).unwrap();
      toast.success("Payment confirmed.");
      setModalPage("");
      setDetailId(null);
    } catch (e) {
      toast.error(e?.data?.message || e?.message || "Failed to confirm");
    }
  };

  const handleReject = async () => {
    if (!detailId || !rejectReason.trim()) {
      toast.error("Please enter a reason.");
      return;
    }
    try {
      await rejectPayment({ id: detailId, reason: rejectReason.trim() }).unwrap();
      toast.success("Request rejected.");
      setModalPage("");
      setDetailId(null);
      setRejectReason("");
    } catch (e) {
      toast.error(e?.data?.message || e?.message || "Failed to reject");
    }
  };

  const detailModal = [
    {
      name: "Detail",
      component: (
        <div className="w-full py-4 px-3 px-lg-4" style={{ maxWidth: "560px" }}>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <h5 className="fw-bold text-black2 mb-0">Bank transfer</h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setModalPage("")}
            />
          </div>
          {detailLoading ? (
            <p className="text-muted">Loading...</p>
          ) : detail ? (
            <div className="small space-y-2">
              {(() => {
                const receiptLinks = getReceiptLinks(detail);
                return (
                  <>
              <p>
                <span className="text-muted">Reference:</span>{" "}
                <strong>{detail.payment_reference}</strong>
              </p>
              <p>
                <span className="text-muted">Amount:</span>{" "}
                {formatMoney(Number(detail.total_amount || 0).toFixed(2), true)}
              </p>
              <p>
                <span className="text-muted">Status:</span> {statusBadge(detail.status)}
              </p>
              {detail.company_bank && (
                <div className="mt-2 p-3 rounded" style={{ background: "#f7f7f7" }}>
                  <p className="mb-1 fw-semibold">Society bank</p>
                  <p className="mb-0">{detail.company_bank.bank_name}</p>
                  <p className="mb-0">{detail.company_bank.account_name}</p>
                  <p className="mb-0">{detail.company_bank.account_number}</p>
                </div>
              )}
              {detail.member_note && (
                <p>
                  <span className="text-muted">Member note:</span> {detail.member_note}
                </p>
              )}
              <div>
                <p className="mb-1">
                  <span className="text-muted">Receipt:</span>
                </p>
                {receiptLinks.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {receiptLinks.map((url, idx) => (
                      <a
                        key={`admin-receipt-${detail?.id || idx}-${idx}`}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        className="small fw-semibold text-secondary2"
                        style={{
                          textDecoration: "underline",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <span
                          className="rounded-pill px-2 py-1"
                          style={{
                            border: "1px solid #e0e0e0",
                            background: "#fff",
                            color: "#425466",
                          }}
                        >
                          {isPdfUrl(url) ? "PDF" : "Image"}
                        </span>
                        View receipt
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="small text-muted mb-0">No receipt added.</p>
                )}
              </div>
              {detail.status === "AWAITING_BANK_CONFIRMATION" && (
                <div className="mt-3 p-3 border border-warning rounded">
                  <p className="small mb-2 fw-semibold text-danger">
                    Confirm only after you have verified that this exact amount is in the
                    society bank account. This action cannot be undone.
                  </p>
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <ButtonComp
                      type="button"
                      btnText={confirming ? "Confirming..." : "Confirm payment"}
                      onClick={() => setModalPage("ConfirmWarn")}
                      disabled={confirming}
                      btnClassName="bg-primary1 text-white px-3 py-2 rounded border-0"
                      ButtonInlineStyle={{
                        whiteSpace: "nowrap",
                        minWidth: "160px",
                      }}
                    />
                    <ButtonComp
                      type="button"
                      btnText="Reject"
                      onClick={() => setModalPage("Reject")}
                      disabled={rejecting}
                      btnClassName="bg-white text-danger px-3 py-2 rounded border border-danger"
                      ButtonInlineStyle={{
                        whiteSpace: "nowrap",
                        minWidth: "120px",
                      }}
                    />
                  </div>
                </div>
              )}
                  </>
                );
              })()}
            </div>
          ) : (
            <p className="text-muted">No data.</p>
          )}
        </div>
      ),
    },
    {
      name: "Reject",
      component: (
        <div className="p-4">
          <p className="fw-semibold mb-2">Reject submission</p>
          <textarea
            className="form-control mb-3"
            rows={4}
            placeholder="Reason (required)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <div className="d-flex gap-2">
            <ButtonComp type="button" btnText="Back" onClick={() => setModalPage("Detail")} />
            <ButtonComp
              type="button"
              btnText={rejecting ? "Rejecting..." : "Reject"}
              onClick={handleReject}
              disabled={rejecting}
              loading={rejecting}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="px-3 px-lg-4 py-4">
      <CardComp cardClassName="border-0 mb-4">
        <div className="px-4 py-3">
          <h2 className="text-black1 text-xl fw-bold mb-0">Confirm bank transfers</h2>
          <p className="text-muted small mb-0">
            Review member bank transfer requests. Confirm only after funds show in your society account.
          </p>
        </div>
      </CardComp>

      <CardComp cardClassName="border-0 py-0 px-0">
        <div className="p-3 d-flex flex-wrap gap-2 align-items-center">
          <select
            className="form-select"
            style={{ maxWidth: "200px" }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="rejected">Rejected</option>
          </select>
          <input
            type="search"
            className="form-control"
            style={{ maxWidth: "260px" }}
            placeholder="Search member..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <TableCompData
          columns={columns}
          data={rows}
          progressPending={isFetching}
          progressComponent={<TableSkeletonLoader rows={10} columns={6} />}
        />
        <div className="p-3 d-flex justify-content-between align-items-center">
          <span className="small text-muted">
            Page {pagination.current_page || page} / {pagination.last_page || 1}
          </span>
          <div className="d-flex gap-2">
            <ButtonComp
              type="button"
              btnText="Prev"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isFetching}
            />
            <ButtonComp
              type="button"
              btnText="Next"
              onClick={() => setPage((p) => p + 1)}
              disabled={
                isFetching ||
                (pagination.last_page && page >= pagination.last_page)
              }
            />
          </div>
        </div>
      </CardComp>

      <ModalCompAdvanceUpdate
        size="md"
        pageName={modalPage}
        centered
        arrayComp={detailModal}
        handleClose={() => {
          setModalPage("");
          setDetailId(null);
        }}
      />
      <ConfirmationModal
        isOpen={modalPage === "ConfirmWarn"}
        onClose={() => setModalPage("Detail")}
        onConfirm={handleConfirm}
        title="Confirm this payment?"
        message={`${
          isLoanRepayment
            ? "This amount will be applied to the member's loan: late fees first, then the installments."
            : "The member's account will be credited for this amount."
        } Only continue after you have verified the transfer in the society account. This action cannot be undone.`}
        confirmText={confirming ? "Confirming..." : "Yes, confirm payment"}
        cancelText="Cancel"
        confirmButtonClass="bg-primary1 text-white"
        cancelButtonClass="bg-white text-dark border"
        isLoading={confirming}
      />
    </div>
  );
}
